import React from 'react';

import { Divider, Grid, Header } from '@mairistem/composants';

import { PanelButtonEdition } from '@mairistem/administres-utils';
import ShowDiffContent from '@/components/Common/ShowDiffContent';
import { useUpdateConvention } from '@/hooks';
import {
  formatDate,
  formatLieu,
  normalizeConventionCodes,
  resolveCasCode,
} from '@/utils';
import {
  OPTIONS_TYPE_CONVENTION_FICHE,
  OPTIONS_AIDE_MATERIELLE,
  OPTIONS_REGIME_BIEN,
} from '@/constants';

import InformationsConventionConditionsModal from './InformationsConventionConditionsModal';
import InformationsConventionSignatureModal from './InformationsConventionSignatureModal';

interface InformationsConventionInitialeProps {
  showDiff?: boolean;
  conventionDeclaration: any;
  onSaved?: () => void;
}

// Sous-sections éditables de ce panneau.
type ConventionSection = 'conditions' | 'signature';

const InformationsConventionInitiale = (
  props: InformationsConventionInitialeProps,
): JSX.Element => {
  const { conventionDeclaration, showDiff, onSaved } = props;

  const { updateConvention } = useUpdateConvention();

  // Une seule modale ouverte à la fois ; brouillon local persisté par un PUT de
  // la convention complète à la sauvegarde.
  const [openSection, setOpenSection] =
    React.useState<ConventionSection | null>(null);
  const [draft, setDraft] = React.useState<any>(null);

  const handleOpenConditions = React.useCallback(() => {
    // Le GET renvoie les libellés ; on les convertit en clés pour le PUT.
    setDraft({
      typeConvention: resolveCasCode(
        OPTIONS_TYPE_CONVENTION_FICHE,
        conventionDeclaration?.typeConvention,
      ),
      aideMaterielle: resolveCasCode(
        OPTIONS_AIDE_MATERIELLE,
        conventionDeclaration?.aideMaterielle,
      ),
      valeurAide: conventionDeclaration?.valeurAide ?? null,
      regimeBien: resolveCasCode(
        OPTIONS_REGIME_BIEN,
        conventionDeclaration?.regimeBien,
      ),
    });
    setOpenSection('conditions');
  }, [conventionDeclaration]);

  const handleOpenSignature = React.useCallback(() => {
    setDraft({
      dateConvention: conventionDeclaration?.dateConvention ?? null,
      adresseConvention: { ...conventionDeclaration?.adresseConvention },
    });
    setOpenSection('signature');
  }, [conventionDeclaration]);

  const handleChange = React.useCallback((data: Record<string, unknown>) => {
    setDraft((prev: any) => ({ ...prev, ...data }));
  }, []);

  const handleClose = React.useCallback(() => {
    setOpenSection(null);
    setDraft(null);
  }, []);

  const handleSave = React.useCallback(() => {
    if (!conventionDeclaration?.identifiant) {
      return Promise.reject(
        new Error('Contexte de convention incomplet pour la sauvegarde'),
      );
    }

    const payload = {
      ...normalizeConventionCodes(conventionDeclaration),
      ...draft,
    };

    return updateConvention(conventionDeclaration.identifiant, payload).then(
      () => {
        onSaved?.();
      },
    );
  }, [conventionDeclaration, draft, updateConvention, onSaved]);

  // Lieu de signature de la convention : `adresseConvention` (et non
  // `adressePacs`, qui est l'adresse d'enregistrement du PACS).
  const adresseConvention = formatLieu(
    conventionDeclaration?.adresseConvention?.localite,
    conventionDeclaration?.adresseConvention?.codePostal,
    conventionDeclaration?.adresseConvention?.libellePays,
  );
  const prevAdresseConvention = formatLieu(
    conventionDeclaration?.previousConvention?.adresseConvention?.localite,
    conventionDeclaration?.previousConvention?.adresseConvention?.codePostal,
    conventionDeclaration?.previousConvention?.adresseConvention?.libellePays,
  );

  const aideMaterielleFormatted = React.useMemo(() => {
    if (conventionDeclaration?.aideMaterielle === 'Fixé à la hauteur de ') {
      return `${conventionDeclaration?.aideMaterielle} ${conventionDeclaration?.valeurAide} euros par an`;
    }
    return conventionDeclaration?.aideMaterielle;
  }, [
    conventionDeclaration?.aideMaterielle,
    conventionDeclaration?.valeurAide,
  ]);

  const prevAideMaterielleFormatted = React.useMemo(() => {
    if (
      conventionDeclaration?.previousConvention?.aideMaterielle ===
      'Fixé à la hauteur de '
    ) {
      return `${conventionDeclaration?.previousConvention?.aideMaterielle} ${conventionDeclaration?.previousConvention?.valeurAide} euros par an`;
    }
    return conventionDeclaration?.previousConvention?.aideMaterielle;
  }, [
    conventionDeclaration?.previousConvention?.aideMaterielle,
    conventionDeclaration?.previousConvention?.valeurAide,
  ]);

  return (
    <Grid>
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            CONDITIONS
          </Header>
          <PanelButtonEdition
            onClick={handleOpenConditions}
            data-testid="panel-edit-modal-convention-conditions"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={7}>
          {showDiff ? (
            <>
              <span>
                <b>Type de convention</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.typeConvention
                }
                newContent={conventionDeclaration?.typeConvention}
              />
            </>
          ) : (
            <>
              <span>
                <b>Type de convention : </b>
              </span>
              <span>{conventionDeclaration?.typeConvention}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Aide matérielle</b>
              </span>
              <ShowDiffContent
                oldContent={prevAideMaterielleFormatted}
                newContent={aideMaterielleFormatted}
              />
            </>
          ) : (
            <>
              <span>
                <b>Aide matérielle : </b>
              </span>
              <span>{aideMaterielleFormatted}</span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          {showDiff ? (
            <>
              <span>
                <b>Régime des biens</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.regimeBien
                }
                newContent={conventionDeclaration?.regimeBien}
              />
            </>
          ) : (
            <>
              <span>
                <b>Régime des biens : </b>
              </span>
              <span>{conventionDeclaration?.regimeBien}</span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      <Divider />
      <Grid.Row>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Header as="h5" color="blue" style={{ margin: 0 }}>
            SIGNATURE
          </Header>
          <PanelButtonEdition
            onClick={handleOpenSignature}
            data-testid="panel-edit-modal-convention-signature"
          />
        </div>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3}>
          {showDiff ? (
            <>
              <span>
                <b>Convention signée le</b>
              </span>
              <ShowDiffContent
                oldContent={formatDate(
                  conventionDeclaration?.previousConvention?.dateConvention,
                )}
                newContent={formatDate(conventionDeclaration?.dateConvention)}
              />
            </>
          ) : (
            <>
              <span>
                <b>Convention signée le : </b>
              </span>
              <span>{formatDate(conventionDeclaration?.dateConvention)}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={4}>
          {showDiff ? (
            <>
              <span>
                <b>À</b>
              </span>
              <ShowDiffContent
                oldContent={prevAdresseConvention}
                newContent={adresseConvention}
              />
            </>
          ) : (
            <>
              <span>
                <b>À : </b>
              </span>
              <span>{adresseConvention}</span>
            </>
          )}
        </Grid.Column>
        <Grid.Column width={5}>
          {showDiff ? (
            <>
              <span>
                <b>Commune déléguée</b>
              </span>
              <ShowDiffContent
                oldContent={
                  conventionDeclaration?.previousConvention?.adresseConvention
                    ?.communeDelegue || 'Non renseignée'
                }
                newContent={
                  conventionDeclaration?.adresseConvention?.communeDelegue ||
                  'Non renseignée'
                }
              />
            </>
          ) : (
            <>
              <span>
                <b>Commune déléguée : </b>
              </span>
              <span>
                {conventionDeclaration?.adresseConvention?.communeDelegue ||
                  'Non renseignée'}
              </span>
            </>
          )}
        </Grid.Column>
      </Grid.Row>
      {openSection === 'conditions' && (
        <InformationsConventionConditionsModal
          data={draft}
          onChange={handleChange}
          onSave={handleSave}
          onClose={handleClose}
          onCancel={handleClose}
        />
      )}
      {openSection === 'signature' && (
        <InformationsConventionSignatureModal
          data={draft}
          onChange={handleChange}
          onSave={handleSave}
          onClose={handleClose}
          onCancel={handleClose}
        />
      )}
    </Grid>
  );
};

export default InformationsConventionInitiale;
